// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  Description,
  H1,
  Illustration,
  InlineAlert,
  LgPadding,
  SmSpacing,
  XsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React from 'react';
import { usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getProgynyWizardFirstFill } from '~shared/selectors/checkout/progyny';
import { useSelectorShared } from '~shared/store';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { progynyAssets } from '../../shared/components/progynyAssets';
import { getProgynyWizardNextRoute } from '../helpers/getProgynyWizardNextRoute';
import { NumberedStep } from './NumberedStep';

export const ProgynyWizardIntro = () => {
  const { getCurrentRouteName, goBack, navigate } = useNavigation();
  const isRefill = !useSelectorShared(getProgynyWizardFirstFill);

  const onNext = () => {
    const currentRoute = getCurrentRouteName();
    const nextRouteName = getProgynyWizardNextRoute(currentRoute);

    if (!nextRouteName) return;
    navigate(nextRouteName);
  };

  usePageViewedEvent({
    event: EVENTS.MEDICATION_MANAGEMENT_OVERVIEW_VIEWED,
    params: {
      is_refill: isRefill,
    },
  });

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      HeaderContent={
        <LgPadding>
          <Column center>
            <Illustration
              size="lg"
              source={progynyAssets.menopur}
            />
            <XsSpacing />
            <H1
              testID="e2e-progyny-step1-header"
              center
            >
              Let's set up your order in just a few steps
            </H1>
          </Column>
        </LgPadding>
      }
      buttons={[
        <Button
          key="Continue"
          onPress={onNext}
          testID="e2e-progyny-step1-button"
        >
          Continue
        </Button>,
      ]}
    >
      <Card>
        <SmSpacing />
        <NumberedStep number={1}>
          <Body
            fontFamily="semibold"
            color={COLORS.TEXT_COLORS.GREY}
          >
            Answer a few questions
          </Body>{' '}
          about your medications and treatment timing
        </NumberedStep>
        <XsSpacing />
        <NumberedStep number={2}>
          <Body
            fontFamily="semibold"
            color={COLORS.TEXT_COLORS.GREY}
          >
            We calculate the exact amount
          </Body>{' '}
          of medication for where you are in your fertility cycle
        </NumberedStep>
        <XsSpacing />
        <NumberedStep number={3}>
          <Body
            fontFamily="semibold"
            color={COLORS.TEXT_COLORS.GREY}
          >
            Select the delivery time
          </Body>{' '}
          that works best for you
        </NumberedStep>
        <LgPadding topPadding={SPACING.STATIC.MD}>
          <InlineAlert>
            <Description>
              These questions will only apply to Gonal-f, Menopur, and Cetrotide. If you have other medications in your
              cart, you'll be able to review your full order later on in checkout.
            </Description>
          </InlineAlert>
        </LgPadding>
      </Card>
    </ProgynyPage>
  );
};
